import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'

const ACTION_CONFIG = {
  invokeLambda: { icon: '\u03BB', color: '#5C6BC0', label: 'Lambda' },
  slack: { icon: '#', color: '#E91E63', label: 'Slack' },
  pass: { icon: '\u2713', color: '#66BB6A', label: 'Pass' },
  fail: { icon: '\u2717', color: '#EF5350', label: 'Fail' },
  start: { icon: '\u25B6', color: '#42A5F5', label: 'Start' },
  workflow: { icon: '\u21BB', color: '#AB47BC', label: 'Workflow' }
}

const DEFAULT_CONFIG = { icon: '?', color: '#90A4AE', label: 'Unknown' }

function shortenIdentifier (id) {
  if (!id) return null
  const fnMatch = id.match(/:function:(.+)$/)
  return fnMatch ? fnMatch[1] : id
}

const STATUS_STYLES = {
  completed: { borderColor: '#66BB6A', backgroundColor: '#E8F5E9' },
  failed: { borderColor: '#EF5350', backgroundColor: '#FFEBEE' },
  running: { borderColor: '#42A5F5', backgroundColor: '#E3F2FD' },
  deferred: { borderColor: '#FFA726', backgroundColor: '#FFF3E0' },
  ignored: { borderColor: '#BDBDBD', backgroundColor: '#F5F5F5', opacity: 0.5 },
  waiting: { borderColor: '#E0E0E0', backgroundColor: '#FAFAFA' }
}

const useStyles = makeStyles({
  node: {
    width: 200,
    minHeight: 64,
    borderRadius: 8,
    border: '2px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    gap: 10,
    cursor: 'pointer',
    transition: 'box-shadow 0.15s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
    }
  },
  selected: {
    boxShadow: '0 0 0 2px #1976D2'
  },
  iconBadge: {
    width: 32,
    height: 32,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: 16,
    flexShrink: 0
  },
  info: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  action: {
    fontSize: 11,
    color: '#757575',
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  asyncBadge: {
    fontSize: 9,
    color: '#FFFFFF',
    backgroundColor: '#FF9800',
    borderRadius: 3,
    padding: '1px 4px',
    fontWeight: 600,
    alignSelf: 'flex-start'
  },
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(66, 165, 245, 0.4)' },
    '70%': { boxShadow: '0 0 0 6px rgba(66, 165, 245, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(66, 165, 245, 0)' }
  },
  running: {
    animation: '$pulse 1.5s infinite'
  }
})

function StepNode ({ data, selected }) {
  const classes = useStyles()
  const actionConfig = ACTION_CONFIG[data.targetAction] ?? DEFAULT_CONFIG
  const status = data.status
  const statusStyle = status ? STATUS_STYLES[status] ?? {} : {}
  const isRunning = status === 'running'

  const nodeStyle = {
    ...statusStyle,
    ...(statusStyle.borderColor ? { borderColor: statusStyle.borderColor } : {})
  }

  const shortId = shortenIdentifier(data.targetIdentifier)
  const targetLabel = data.target || shortId
    ? `${actionConfig.label}${data.target ? ` \u2192 ${data.target}` : ''}${shortId ? ` \u2192 ${shortId}` : ''}`
    : actionConfig.label

  return (
    <div
      className={`${classes.node} ${selected ? classes.selected : ''} ${isRunning ? classes.running : ''}`}
      style={nodeStyle}
    >
      <div className={classes.iconBadge} style={{ backgroundColor: actionConfig.color }}>
        {actionConfig.icon}
      </div>
      <div className={classes.info}>
        <div className={classes.name} title={data.name}>
          {data.name}
        </div>
        <div className={classes.action} title={targetLabel}>
          {targetLabel}
        </div>
        {data.isAsync ? <span className={classes.asyncBadge}>ASYNC</span> : null}
      </div>
    </div>
  )
}

StepNode.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    targetAction: PropTypes.string,
    target: PropTypes.string,
    targetIdentifier: PropTypes.string,
    isAsync: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    status: PropTypes.string
  }),
  selected: PropTypes.bool
}

export default StepNode
